/**
 * Core domain vocabulary for CLEAR.
 *
 * Naming: an *assembly line* is the physical line on the floor (e.g. "Line 2").
 * A *plan item* is one row of a shift plan (model × colour × quantity). The PRD
 * calls plan items "plan lines"; the code avoids that name to keep the two apart.
 */

export const GATES = ['paint', 'rm', 'quality'] as const;
export type Gate = (typeof GATES)[number];

export const ROLES = ['planner', 'paint', 'rm', 'quality', 'supervisor', 'mgmt', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const PRIORITIES = ['high', 'normal', 'low'] as const;
export type Priority = (typeof PRIORITIES)[number];

/**
 * Stored gate status. Labels differ per gate (see i18n):
 *  - paint:   ok = OK, partial = Partial,      blocked = Short
 *  - rm:      ok = OK, partial = Partial,      blocked = Not available
 *  - quality: ok = OK, partial = Partial hold, blocked = Hold
 */
export const GATE_STATUSES = ['ok', 'partial', 'blocked'] as const;
export type GateStatus = (typeof GATE_STATUSES)[number];

/** Final status of a plan item. PART = "Part ready". */
export const ITEM_STATUSES = ['READY', 'PART', 'WAIT', 'HOLD', 'STARTED'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

export type PlanStatus = 'draft' | 'published';

/** The current (latest, non-undone) entry of one gate on one plan item. */
export interface GateState {
  entryId: string;
  status: GateStatus;
  /** Quantity cleared by this entry. For OK it is the full clearable quantity at the time of the tap. */
  okQty: number;
  reasonId: string | null;
  userId: string;
  at: number;
}

export interface PlanItemState {
  id: string;
  qty: number;
  priority: Priority;
  runOrder: number;
  cancelled: boolean;
  startedAt: number | null;
  /** When the item became visible to departments (publish time, or add time if added after publish). */
  activeSince: number;
  gates: Partial<Record<Gate, GateState>>;
}

export const PRIORITY_RANK: Record<Priority, number> = { high: 0, normal: 1, low: 2 };

/** Icons available for reason chips (drawn by the UI kit). */
export const REASON_ICONS = [
  'warn',
  'box',
  'palette',
  'tool',
  'truck',
  'swap',
  'scale',
  'repeat',
  'scratch',
  'ruler',
  'flask',
  'doc',
  'clock',
  'eye',
] as const;
export type ReasonIconName = (typeof REASON_ICONS)[number];
